import { z } from 'zod/v4';

/**
 * Направление текста
 */
export const textDirectionSchema = z.enum(['ltr', 'rtl']);

/**
 * Схема языка
 */
export const languageSchema = z.object({
  code: z.string()
    .min(2, 'Код языка должен быть минимум 2 символа')
    .max(5, 'Код языка должен быть максимум 5 символов'),
  name: z.string()
    .min(1, 'Название обязательно')
    .max(100, 'Название слишком длинное (максимум 100 символов)'),
  nativeName: z.string()
    .min(1, 'Родное название обязательно')
    .max(100, 'Родное название слишком длинное (максимум 100 символов)'),
  flagEmoji: z.string()
    .min(1, 'Флаг обязателен')
    .max(10, 'Флаг слишком длинный'),
  isActive: z.boolean(),
  isDefault: z.boolean(),
  textDirection: textDirectionSchema,
});

/**
 * Схема создания языка
 */
export const createLanguageSchema = z.object({
  code: z.string()
    .min(2, 'Код языка должен быть минимум 2 символа')
    .max(5, 'Код языка должен быть максимум 5 символов'),
  name: z.string()
    .min(1, 'Название обязательно')
    .max(100, 'Название слишком длинное (максимум 100 символов)'),
  nativeName: z.string()
    .min(1, 'Родное название обязательно')
    .max(100, 'Родное название слишком длинное (максимум 100 символов)'),
  flagEmoji: z.string()
    .min(1, 'Флаг обязателен')
    .max(10, 'Флаг слишком длинный'),
  textDirection: textDirectionSchema.optional(),
});

export type CreateLanguageInput = z.infer<typeof createLanguageSchema>;

/**
 * Схема обновления языка
 */
export const updateLanguageSchema = z.object({
  name: z.string()
    .min(1, 'Название обязательно')
    .max(100, 'Название слишком длинное (максимум 100 символов)')
    .optional(),
  nativeName: z.string()
    .min(1, 'Родное название обязательно')
    .max(100, 'Родное название слишком длинное (максимум 100 символов)')
    .optional(),
  flagEmoji: z.string()
    .min(1, 'Флаг обязателен')
    .max(10, 'Флаг слишком длинный')
    .optional(),
  textDirection: textDirectionSchema.optional(),
});

export type UpdateLanguageInput = z.infer<typeof updateLanguageSchema>;

/**
 * Параметр кода языка
 */
export const languageCodeParamSchema = z.object({
  code: z.string()
    .min(2, 'Код языка должен быть минимум 2 символа')
    .max(5, 'Код языка должен быть максимум 5 символов'),
});

/**
 * Типы сущностей для переводов
 */
export const translatableEntityTypes = [
  'course',
  'module',
  'moduleItem',
  'article',
  'banner',
  'subscriptionPlan',
  'subscriptionBenefit',
  'teacher',
  'goal',
  'reminder',
] as const;

/**
 * Схема типа сущности
 */
export const entityTypeSchema = z.enum([
  'course',
  'module',
  'moduleItem',
  'article',
  'banner',
  'subscriptionPlan',
  'subscriptionBenefit',
  'teacher',
  'goal',
  'reminder',
]);

/**
 * Параметры для работы с переводами контента
 */
export const contentTranslationParamsSchema = z.object({
  entityType: entityTypeSchema,
  entityId: z.string(),
});

/**
 * Параметры для работы с переводами контента с языком
 */
export const contentTranslationWithLanguageParamsSchema = z.object({
  entityType: entityTypeSchema,
  entityId: z.string(),
  language: z.string()
    .min(2, 'Код языка должен быть минимум 2 символа')
    .max(5, 'Код языка должен быть максимум 5 символов'),
});

/**
 * Схема данных перевода контента (динамическая)
 */
export const contentTranslationDataSchema = z.record(z.string(), z.string().optional());

/**
 * Схема UI переводов
 */
export const uiTranslationsSchema = z.record(z.string(), z.string());

export type UITranslationsInput = z.infer<typeof uiTranslationsSchema>;

/**
 * Параметр языка для UI переводов
 */
export const uiLanguageParamSchema = z.object({
  language: z.string().min(2).max(5),
});

/**
 * Схема для пагинации (localization-specific)
 */
export const localizationPaginationSchema = z.object({
  page: z.coerce.number().min(1, 'Страница должна быть >= 1').default(1).optional(),
  limit: z.coerce.number()
    .min(1, 'Лимит должен быть >= 1')
    .max(100, 'Лимит должен быть <= 100')
    .default(20)
    .optional(),
  search: z.string().optional(),
  sortBy: z.string().optional(),
  sortOrder: z.string().optional(),
});

export type LocalizationPaginationInput = z.infer<typeof localizationPaginationSchema>;

/**
 * Схема для фильтра языка в query
 */
export const languageQuerySchema = z.object({
  lang: z.string()
    .min(2, 'Код языка должен быть минимум 2 символа')
    .max(5, 'Код языка должен быть максимум 5 символов')
    .optional(),
});

/**
 * Формат экспорта
 */
export const exportFormatSchema = z.object({
  format: z.enum(['json', 'xlsx']).optional(),
});

/**
 * Параметры статистики по типу сущности
 */
export const statsEntityTypeParamsSchema = z.object({
  entityType: entityTypeSchema,
});

/**
 * Параметры для недостающих переводов
 */
export const missingTranslationsParamsSchema = z.object({
  entityType: entityTypeSchema,
  language: z.string()
    .min(2, 'Код языка должен быть минимум 2 символа')
    .max(5, 'Код языка должен быть максимум 5 символов'),
});

/**
 * Схема для toggle активности языка
 */
export const toggleActiveSchema = z.object({
  isActive: z.boolean(),
});

/**
 * Схема для синхронизации языков
 */
export const syncLanguagesSchema = z.object({
  language: z.string()
    .min(2, 'Код языка должен быть минимум 2 символа')
    .max(5, 'Код языка должен быть максимум 5 символов')
    .optional(),
});

export type SyncLanguagesInput = z.infer<typeof syncLanguagesSchema>;

/**
 * Схема для экспорта переводов
 */
export const exportTranslationsQuerySchema = z.object({
  format: z.enum(['json', 'xlsx']).optional(),
});

export type ExportTranslationsQuery = z.infer<typeof exportTranslationsQuerySchema>;

/**
 * Схема для импорта переводов (body содержит File, валидируется на уровне Elysia)
 */
export const importTranslationsBodySchema = z.object({
  file: z.any(), // File объект, валидируется Elysia
});

/**
 * Параметр ключа для UI переводов (dot-notation, e.g. "privileges.channels")
 */
export const uiTranslationKeyParamSchema = z.object({
  key: z.string().min(1, 'Ключ перевода обязателен').max(255, 'Ключ перевода слишком длинный'),
});

/**
 * Body для создания/обновления UI перевода по ключу
 * { translations: { ru: "Подписки", en: "Subscriptions", uz: "Obunalar" } }
 */
export const upsertTranslationKeyBodySchema = z.object({
  translations: z.record(z.string(), z.string()),
});

export type UpsertTranslationKeyInput = z.infer<typeof upsertTranslationKeyBodySchema>;

/**
 * Body для создания нового UI ключа перевода
 * { key: "privileges.channels", translations: { ru: "Подписки", en: "Subscriptions" } }
 */
export const createTranslationKeyBodySchema = z.object({
  key: z.string().min(1, 'Ключ перевода обязателен').max(255, 'Ключ перевода слишком длинный'),
  translations: z.record(z.string(), z.string()),
});

export type CreateTranslationKeyInput = z.infer<typeof createTranslationKeyBodySchema>;

/**
 * Схема для списка UI переводов (query параметры)
 */
export const uiTranslationsListQuerySchema = z.object({
  namespace: z.string().optional(),
  key: z.string().optional(),
  page: z.coerce.number().min(1, 'Страница должна быть >= 1').optional(),
  limit: z.coerce.number()
    .min(1, 'Лимит должен быть >= 1')
    .max(200, 'Лимит должен быть <= 200')
    .optional(),
});

export type UITranslationsListQuery = z.infer<typeof uiTranslationsListQuerySchema>;

// ==================== Response Types ====================

/**
 * Tolgee Key структура
 */
export interface TolgeeKey {
  id: number;
  name: string;
  namespace?: string;
}

/**
 * Tolgee Translation структура
 */
export interface TolgeeTranslation {
  id: number;
  text: string | null;
  state: string;
}

/**
 * Tolgee Key с переводами
 */
export interface TolgeeKeyWithTranslations {
  keyId: number;
  keyName: string;
  keyNamespace?: string;
  translations: Record<string, TolgeeTranslation>;
}

/**
 * Ответ API Tolgee для списка ключей
 */
export interface TolgeeKeysResponse {
  _embedded?: {
    keys?: TolgeeKey[];
  };
  page?: {
    totalElements?: number;
  };
}

/**
 * Ответ API Tolgee для ключей с переводами
 */
export interface TolgeeTranslationsResponse {
  _embedded?: {
    keys?: TolgeeKeyWithTranslations[];
  };
  page?: {
    totalElements?: number;
    number?: number;
    size?: number;
  };
}
